
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDealers } from "@/hooks/dealers/useDealers";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";
import { toast } from "sonner";
import { matchesCity, formatCityFromSlug } from "@/utils/cityUtils";

export function useDealersByCity() {
  const { city } = useParams<{ city: string }>();
  const navigate = useNavigate();
  const { dealers, isLoading, filters, updateFilters, refreshDealers } = useDealers();
  const { formatCityName } = useDealerUtils();
  const [view, setView] = useState<"list" | "map">("list");
  
  // Format city name for display (convert-slug-to Title Case)
  const formattedCity = city
    ? formatCityName(city)
    : '';
  
  // Reset any previous search filter when city changes
  // and apply a more lenient city filter in the component
  useEffect(() => {
    if (city) {
      // Clear previous search filter to avoid restricting results
      updateFilters({ search: '' });
    }
  }, [city, updateFilters]);
  
  const handleCardClick = (dealerId: string) => {
    navigate(`/compro-oro/${city}/${dealerId}`);
  };
  
  const handleGoBack = () => {
    navigate('/compro-oro');
  };
  
  const handleRefresh = () => {
    toast.info("Aggiornamento dati in corso...");
    refreshDealers();
  };

  const handleDealerSelect = (dealer: any) => {
    // Always use the current city from the URL parameter
    // This ensures consistency with the current page context
    if (city) {
      navigate(`/compro-oro/${city}/${dealer.id}`);
    } else {
      // Fallback to dealer's city or default
      const citySlug = dealer.city 
        ? dealer.city.toLowerCase().replace(/[^\w\s]/gi, '').replace(/\s+/g, '-')
        : 'roma';
      navigate(`/compro-oro/${citySlug}/${dealer.id}`);
    }
  };
  
  // Filter dealers based on the city using improved matching
  const cityDealers = dealers.filter(dealer => {
    if (!city) return false;
    
    // Use the new city matching utility for precise filtering
    return matchesCity(dealer.city || '', city) || 
           matchesCity(dealer.address || '', city);
  });

  return {
    cityDealers,
    isLoading,
    filters,
    formattedCity,
    city,
    view,
    setView,
    updateFilters,
    handleCardClick,
    handleGoBack,
    handleRefresh,
    handleDealerSelect,
    refreshDealers
  };
}
